/* eslint-disable react/prop-types */
import React, {
  useContext,
  useEffect
} from 'react';
import { ELSButton } from '@els/els-component-button-react';
import { FlexLayout } from '../../components/flex/FlexLayout.component';
import { FlexItem } from '../../components/flex/FlexItem.component';
import { FlexLayoutModifier } from '../../components/flex/flex.constants';
import ActivityProgressOverview from './ActivityProgressOverview.component';
import { UserDto } from '../../apis/eols-user-management-service/eols-user-management-service.dtos';
import {
  HesiExamInfoDto,
  RaasAssessmentDto,
  RecommendationGenerationStatusDto,
  RemediationActivityTypeSettings,
  RemediationAssignmentSourceTypeDto
} from '../../apis/nhe-remediation-service/nhe-remediation-service.dtos';
import {
  ELSButtonSize,
  ELSButtonType
} from '../../models/button.models';
import {
  getActiveAssignmentActivityTypeSettings,
  getAssessmentGoals,
  getHourAndMinuteFromSecond,
} from '../../utilities/remediation.utilities';
import { ELSDropDown } from '../../components/els.components';
import {
  getAssessmentDropdownOptions,
  isAssessmentHesiSource,
  isAssignmentHesiSource,
  isSingleSourceAssessment,
} from './remediation-student-activity.utilities';
import {
  fetchHesiExamInfoByExamEditionIdAction,
  trackAdobeAction,
  trackNewRelicAction
} from '../../components/app/app-state-actions.utilities';
import { AppContext } from '../../components/app/AppProvider.component';
import {
  EmptyValueDisplay,
  NotApplicableDisplay,
  RemediationAssignmentSourceTypeDisplayName
} from './remediation-student-activity.constants';
import moment from 'moment';
import {
  ELSPropsFromModalService,
  ELSWithModalService
} from '@els/els-component-modal-react';
import { flowRight } from 'lodash';
import FocusTrap from 'focus-trap-react';
import {
  MultipleAssessmentSourceModal,
  MultipleAssessmentSourceModalId
} from './MultipleAssessmentSourceModal.component';
import DueDateProgress from '../../components/due-date-progress/DueDateProgress.component';
import {
  AdobeAnalyticsAction,
  AdobeAnalyticsButtonType
} from '../../models/adobe-analytics.models';
import { NewRelicAnalyticsAction } from '../../models/new-relic-analytics.models';

export type StudentProgressOverviewPropsOnly = {
  selectedAssessmentDto: RaasAssessmentDto;
  usersByIdDto: Record<string, UserDto>;
  raasAssessmentDtos: RaasAssessmentDto[];
  handleSelectAssessment: (assessmentId: number) => void;
};

export type StudentProgressOverviewProps = StudentProgressOverviewPropsOnly & ELSPropsFromModalService;

export type StudentProgressOverviewState = {
  hesiExamInfoDto: HesiExamInfoDto;
  isLoading: boolean;
}

export const defaultState: StudentProgressOverviewState = {
  hesiExamInfoDto: null,
  isLoading: true
};

// eslint-disable-next-line sonarjs/cognitive-complexity
const StudentProgressOverviewComponent = (props: StudentProgressOverviewProps) => {
  const [state, setState] = React.useState<StudentProgressOverviewState>(defaultState);

  const {
    appState,
    setAppState,
    appStateRef
  } = useContext(AppContext);
  const baseState = appState.remediationBaseState;

  useEffect(() => {
    const isHesiSource = isAssignmentHesiSource(baseState.remediationAssignmentWithSources?.sourceList)
      && isAssessmentHesiSource(baseState.raasAssessmentSourceList);

    if (isHesiSource) {
      const editionId = Number(baseState.remediationAssignmentWithSources?.sourceList[0].sourceId);

      fetchHesiExamInfoByExamEditionIdAction({
        appState,
        appStateRef,
        setAppState,
        editionId,
        useCache: false
      })
        .then((response) => {
          setState((prevState => ({
            ...prevState,
            hesiExamInfoDto: response,
          })));
        })
        .catch(() => {
          return null;
        });
    }
  }, [baseState?.raasAssessmentSourceList, baseState?.remediationAssignmentWithSources?.sourceList]);

  if (appState.pendingRequestCount > 0 || !baseState?.remediationAssignmentWithSources || !props.raasAssessmentDtos || !props.selectedAssessmentDto) {
    return null;
  }

  const handleMultipleAssessmentSourceModalClick = () => {
    const {
      modalService,
    } = props;

    if (!baseState || !baseState.raasAssessmentSourceList) {
      return;
    }

    modalService.openCustomModal({
      modalId: MultipleAssessmentSourceModalId,
      noFocusTrap: true,
      modal: (
        <FocusTrap>
          <MultipleAssessmentSourceModal
            hesiExamInfoDto={state.hesiExamInfoDto}
            modalService={modalService}
          />
        </FocusTrap>
      )
    });
  };

  const isAssessmentSelected = (): boolean => {
    return !!(baseState && props.selectedAssessmentDto && props.selectedAssessmentDto.id);
  };

  const getSingleSourceAssessmentName = (): string => {
    if (!baseState?.raasAssessmentSourceList) {
      return NotApplicableDisplay;
    }

    const isHesiSource = isAssignmentHesiSource(baseState.remediationAssignmentWithSources?.sourceList);
    if (isHesiSource && state.hesiExamInfoDto) {
      return state.hesiExamInfoDto.examName;
    }

    return NotApplicableDisplay;
  };

  const getSingleSourceAssessmentSubtype = (): string => {
    if (!baseState?.raasAssessmentSourceList) {
      return '';
    }

    const isHesiSource = isAssignmentHesiSource(baseState.remediationAssignmentWithSources?.sourceList);
    if (isHesiSource && state.hesiExamInfoDto) {
      return RemediationAssignmentSourceTypeDisplayName[RemediationAssignmentSourceTypeDto.HESI_EXAM_EDITION__EDITION_ID];
    }

    return '';
  };

  const getAssessmentSourceDisplay = () => {
    if (!baseState?.raasAssessmentSourceList || !baseState?.raasAssessmentSourceList?.length) {
      return NotApplicableDisplay;
    }

    if (isSingleSourceAssessment(baseState.raasAssessmentSourceList)) {
      const sourceName = getSingleSourceAssessmentName();
      return sourceName === NotApplicableDisplay
        ? sourceName
        : `${sourceName} • ${getSingleSourceAssessmentSubtype()}`;
    }

    return (
      <button
        type="button"
        className="u-els-anchorize u-els-debuttonize"
        onClick={handleMultipleAssessmentSourceModalClick}
      >
        See sources
      </button>
    );
  };

  const getSingleSourceAssessmentScore = (): string => {
    if (!baseState?.raasAssessmentSourceList || !baseState?.raasAssessmentSourceList?.length) {
      return '';
    }

    const [singleSource] = baseState.raasAssessmentSourceList;
    if (!singleSource.score) {
      return NotApplicableDisplay;
    }

    if (isAssessmentHesiSource(baseState.raasAssessmentSourceList)) {
      return `${Math.round(singleSource.score)}`;
    }

    return NotApplicableDisplay;
  };

  const getRaasAssessmentLastAccessedDisplay = (): string => {
    if (!baseState?.raasAssessment) {
      return '';
    }

    const { lastAccessed } = baseState.raasAssessment;
    if (!lastAccessed) {
      return 'Not started';
    }

    // Compare calendar days, not elapsed duration: moment#diff truncates toward zero, so a
    // visit 11 hours ago on the previous calendar day would otherwise read as 0 days -> "Today"
    const differenceInDays = moment().startOf('day').diff(moment(lastAccessed).startOf('day'), 'days');

    if (differenceInDays === 0) {
      return 'Today';
    }

    if (differenceInDays === 1) {
      return 'Yesterday';
    }

    return `${differenceInDays} days ago`;
  };

  const getTotalTimeSpent = (): number => {
    if (!baseState?.assessmentMetrics) {
        return 0;
    }

    const {
      assessContent,
      applyContent,
      reviewContent
    } = baseState.assessmentMetrics;

    return [reviewContent, applyContent, assessContent]
      .reduce(
(total, content) =>
              total + (moment.duration(content?.timeSpent).asSeconds() || 0),
          0
      );
  };

  const getTotalTimeSpentDisplay = (): string => {
    const totalTimeSpent = getTotalTimeSpent();

    return totalTimeSpent > 0 ? getHourAndMinuteFromSecond(totalTimeSpent) : EmptyValueDisplay;
  };

  const mobileFlexItemClasses = ['u-els-width-1o1@mobile', 'u-els-margin-bottom@mobile'];
  const selectedUser = props.usersByIdDto[props.selectedAssessmentDto.userId];

  if (!isAssessmentSelected() || !selectedUser) {
    return null;
  }

  const activeAssessmentDtos = props.raasAssessmentDtos.filter((assessment: RaasAssessmentDto) =>
    assessment.isActive && assessment.recGenerationStatus === RecommendationGenerationStatusDto.COMPLETED);
  const dropDownOptions = getAssessmentDropdownOptions(baseState?.remediationAssignmentWithSources, activeAssessmentDtos, props.usersByIdDto);

  return (
    <>
      <div>
        <div className="c-nra-remediation-student-activity__context">
          <FlexLayout modifiers={[FlexLayoutModifier.GUTTERS_2X, FlexLayoutModifier.MIDDLE]}>
            <FlexItem modifiers={[FlexLayoutModifier.GROW]}>
              <FlexLayout classes={['u-els-font-size-body-large']} modifiers={[FlexLayoutModifier.MIDDLE, FlexLayoutModifier.LEFT]}>
                <FlexItem>
                  <span className=" u-els-margin-right-1o2">Viewing Activity for</span>
                </FlexItem>
                <FlexItem isRender={dropDownOptions && dropDownOptions.length > 1}>
                  <ELSDropDown
                    value={props.selectedAssessmentDto.id}
                    options={dropDownOptions}
                    changeHandler={(_, eolsUserId: string) => props.handleSelectAssessment(Number(eolsUserId))}
                  />
                </FlexItem>
                <FlexItem isRender={dropDownOptions && dropDownOptions.length === 1}>
                  <span>
                    {`${selectedUser.lastName}, ${selectedUser.firstName}`}
                  </span>
                </FlexItem>
              </FlexLayout>
            </FlexItem>
            <FlexItem>
              <DueDateProgress />
            </FlexItem>
          </FlexLayout>
        </div>

        <div className="c-nra-remediation-student-activity__divider-full-page" />

        <div className="c-nra-remediation-student-activity__progress-overview">
          <FlexLayout
            classes={['u-els-margin-bottom-2x']}
            modifiers={[
              FlexLayoutModifier.LEFT,
              FlexLayoutModifier.MIDDLE,
              FlexLayoutModifier.GUTTERS_2X,
              FlexLayoutModifier.WRAP_AT_MOBILE
            ]}
          >
            <FlexItem
              modifiers={[FlexLayoutModifier.GROW]}
              isRender={
                !!(
                  baseState.eolsAssessment?.assessmentGoals?.length
                )
              }
              classes={mobileFlexItemClasses}
            >
              <div>
                {`This plan was generated ${isSingleSourceAssessment(baseState.raasAssessmentSourceList) ? 'from' : 'based on multiple sources'}:`}
              </div>
              <div className="u-els-font-size-body-large">{getAssessmentSourceDisplay()}</div>
            </FlexItem>
            <FlexItem
              isRender={isSingleSourceAssessment(baseState.raasAssessmentSourceList)}
              modifiers={[FlexLayoutModifier.GROW]}
              classes={mobileFlexItemClasses}
            >
              <div>Score</div>
              <div className="u-els-font-size-body-large">
                {getSingleSourceAssessmentScore()}
              </div>
            </FlexItem>
            <FlexItem
              modifiers={[FlexLayoutModifier.GROW]}
              classes={mobileFlexItemClasses}
            >
              <div>Assignment Last Accessed</div>
              <div className="u-els-font-size-body-large">
                {getRaasAssessmentLastAccessedDisplay()}
              </div>
            </FlexItem>
            <FlexItem
              modifiers={[FlexLayoutModifier.GROW]}
              classes={mobileFlexItemClasses}
            >
              <div>Total Time Spent</div>
              <div className="u-els-font-size-body-large">
                {getTotalTimeSpentDisplay()}
              </div>
            </FlexItem>
            <FlexItem
              modifiers={[FlexLayoutModifier.GROW]}
              classes={['u-els-text-align-right', ...mobileFlexItemClasses]}
            >
              <ELSButton
                type={ELSButtonType.SECONDARY}
                size={ELSButtonSize.SMALL}
                onClick={() => {
                  trackAdobeAction({
                    appStateRef,
                    actionProps: {
                      action: AdobeAnalyticsAction.BUTTON_CLICK,
                      pageData: {
                        buttonType: `plp:${AdobeAnalyticsButtonType.ANCHOR}:emailAddress`,
                      }
                    }
                  });
                  trackNewRelicAction({
                    appStateRef,
                    actionProps: {
                      action: NewRelicAnalyticsAction.EMAIL_BUTTON_CLICKED,
                      props: {
                        studentUserId: selectedUser.id,
                        studentStudyEolsAssessmentId: props.selectedAssessmentDto.id,
                        remediationAssignmentId: baseState.remediationAssignmentWithSources?.remediationAssignment?.id
                      }
                    }
                  });
                  window.location.href = `mailto:${selectedUser.emailAddress}`;
                }}
              >
                Email
              </ELSButton>
            </FlexItem>
          </FlexLayout>

          <div className="c-nra-remediation-student-activity__divider" />

          <div className="u-els-padding-top-2x u-els-padding-bottom-2x">
            <FlexLayout
              modifiers={[
                FlexLayoutModifier.GUTTERS,
                FlexLayoutModifier.WRAP_AT_MOBILE
              ]}>
              {
                getActiveAssignmentActivityTypeSettings(baseState)
                  .map((activityType: RemediationActivityTypeSettings) => {
                    return (
                      <FlexItem
                        modifiers={[FlexLayoutModifier.GROW]}
                        key={activityType.activityType}
                        classes={[...mobileFlexItemClasses, 'c-nra-activity-progress-overview__item']}
                      >
                        <ActivityProgressOverview
                          activityType={activityType.activityType}
                          assessmentGoals={getAssessmentGoals(baseState?.eolsAssessment)}
                        />
                      </FlexItem>
                    );
                  })
              }
            </FlexLayout>
          </div>
        </div>
      </div>
    </>
  );
};

const enhance = flowRight(ELSWithModalService);

const StudentProgressOverview: React.FC<StudentProgressOverviewPropsOnly> = enhance(StudentProgressOverviewComponent);

export default StudentProgressOverview;
